import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ApiResponse,
  AccountsData,
  CreateAccountRequest,
  CreateAccountData,
  DeleteAccountRequest,
  DeleteAccountData,
  AccountBalanceData,
  FundAccountRequest,
  FundAccountData,
  TransferAssetRequest,
  TransferAssetData,
  EstablishTrustRequest,
  EstablishTrustData,
  ParsedAccount,
  Account,
  TransactionHistoryData,
} from '../models';

export function fetchAccountBalanceOnce(http: HttpClient, accountPub: string): Promise<AccountBalanceData> {
  return firstValueFrom(
    http
      .get<ApiResponse<AccountBalanceData>>(`/accounts/balance?account_pub=${encodeURIComponent(accountPub)}`)
      .pipe(map((response) => response.data))
  );
}

function parseAccountInfo(account: Account): ParsedAccount {
  const [name, email, phone] = account.info.split(':');
  return {
    id: account.id,
    pub: account.pub,
    name: name || '',
    email: email || '',
    phone: phone || '',
    accountNumber: account.account_number,
  };
}

// Queries
export function injectAccountsQuery(bankPub: () => string) {
  const http = inject(HttpClient);

  return injectQuery(() => ({
    queryKey: ['accounts', bankPub()],
    queryFn: () =>
      firstValueFrom(
        http.get<ApiResponse<AccountsData>>(`/accounts?bank_pub=${bankPub()}`).pipe(
          map((response) => response.data.accounts.map(parseAccountInfo))
        )
      ),
    enabled: !!bankPub(),
  }));
}

export function injectAccountBalanceQuery(accountPub: () => string) {
  const http = inject(HttpClient);

  return injectQuery(() => ({
    queryKey: ['accountBalance', accountPub()],
    queryFn: () =>
      firstValueFrom(
        http.get<ApiResponse<AccountBalanceData>>(`/accounts/balance?account_pub=${accountPub()}`).pipe(
          map((response) => response.data)
        )
      ),
    enabled: !!accountPub(),
  }));
}

export function injectTransactionHistoryQuery(accountPub: () => string) {
  const http = inject(HttpClient);

  return injectQuery(() => ({
    queryKey: ['transactionHistory', accountPub()],
    queryFn: () =>
      firstValueFrom(
        http.get<ApiResponse<TransactionHistoryData>>(`/external/transactions/${encodeURIComponent(accountPub())}`).pipe(
          map((response) => response.data)
        )
      ),
    enabled: !!accountPub(),
  }));
}

// Mutations
export function injectCreateAccountMutation(bankPub: () => string) {
  const http = inject(HttpClient);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (data: CreateAccountRequest) =>
      firstValueFrom(
        http.post<ApiResponse<CreateAccountData>>('/admin/accounts', data).pipe(
          map((response) => response.data)
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', bankPub()] });
    },
  }));
}

export function injectDeleteAccountMutation(bankPub: () => string) {
  const http = inject(HttpClient);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (data: DeleteAccountRequest) =>
      firstValueFrom(
        http.delete<ApiResponse<DeleteAccountData>>('/admin/accounts', { body: data }).pipe(
          map((response) => response.data)
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', bankPub()] });
    },
  }));
}

export function injectFundAccountMutation(bankPub: () => string) {
  const http = inject(HttpClient);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (data: FundAccountRequest) =>
      firstValueFrom(
        http.post<ApiResponse<FundAccountData>>('/assets/fund', data).pipe(
          map((response) => response.data)
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', bankPub()] });
      queryClient.invalidateQueries({ queryKey: ['accountBalance'] });
    },
  }));
}

export function injectTransferAssetMutation(bankPub: () => string) {
  const http = inject(HttpClient);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (data: TransferAssetRequest) =>
      firstValueFrom(
        http.post<ApiResponse<TransferAssetData>>('/assets/transfer', data).pipe(
          map((response) => response.data)
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', bankPub()] });
      queryClient.invalidateQueries({ queryKey: ['accountBalance'] });
    },
  }));
}

export function injectEstablishTrustMutation() {
  const http = inject(HttpClient);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (data: EstablishTrustRequest) =>
      firstValueFrom(
        http.post<ApiResponse<EstablishTrustData>>('/assets/trust', data).pipe(
          map((response) => response.data)
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accountBalance'] });
    },
  }));
}
